import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Link } from "react-router-dom";
import { Truck, Gavel, DollarSign, BellRing, ArrowRight, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { fmtMoney, STATUS_LABEL, ROLE_LABEL } from "../lib/format";

const KPI = ({ label, value, icon: Icon, accent }) => (
  <div className="kpi-card" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between">
      <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-bold">{label}</div>
      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${accent ? "bg-gold/15 text-navy" : "bg-slate-100 text-slate-500"}`}>
        <Icon size={16} strokeWidth={1.5} />
      </div>
    </div>
    <div className="font-display text-3xl md:text-4xl font-bold tracking-tighter text-navy">{value}</div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [billing, setBilling] = useState(null);
  const [plans, setPlans] = useState({});
  const [packs, setPacks] = useState({});
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [busy, setBusy] = useState("");
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/billing/me"),
      api.get("/billing/plans"),
      api.get("/billing/credit-packs"),
    ]).then(([m, p, c]) => {
      setBilling(m.data);
      setPlans(p.data);
      setPacks(c.data);
    });
  }, []);

  useEffect(() => {
    if (!billing?.billing_access?.has_access) return;
    api.get("/dashboard/stats").then(r => setStats(r.data));
    api.get("/loads", { params: { scope: "mine" } }).then(r => setRecent(r.data.slice(0, 5)));
  }, [billing]);

  if (!billing) return <div className="text-slate-500">Loading…</div>;

  const subscribe = async (plan) => {
    setBusy(`plan-${plan}`);
    setBanner(null);
    try {
      const r = await api.post("/billing/checkout", { plan, cycle: "monthly" });
      window.location.href = r.data.url;
    } catch (e) {
      setBanner({ kind: "error", text: e.response?.data?.detail || "Checkout failed" });
      setBusy("");
    }
  };

  const buyPack = async (pack) => {
    setBusy(`pack-${pack}`);
    setBanner(null);
    try {
      const r = await api.post("/billing/buy-credits", { pack });
      window.location.href = r.data.url;
    } catch (e) {
      setBanner({ kind: "error", text: e.response?.data?.detail || "Checkout failed" });
      setBusy("");
    }
  };

  const audience = billing.audience || "broker";
  const audienceLabel = { driver: "drivers", broker: "brokers & carriers", shipper: "shippers" }[audience];
  const audiencePlanKeys = Object.keys(plans).filter((k) => plans[k].audience === audience);
  const isLocked = !billing.billing_access?.has_access;

  if (isLocked) {
    return (
      <div className="space-y-7" data-testid="payment-gated-dashboard">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-gold font-bold mb-2">{ROLE_LABEL[user.role]} Dashboard</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter text-navy">Activate your account</h1>
          <p className="text-slate-500 mt-2 max-w-2xl">
            Choose a plan for your role to unlock the load board, tracking, documents, bids, and account tools.
          </p>
        </div>

        {banner && (
          <div className="card-base p-4 flex items-start gap-3 border-red-300 bg-red-50" data-testid="dashboard-billing-banner">
            <AlertCircle className="text-red-600 flex-shrink-0" />
            <div className="flex-1 text-sm font-semibold text-slate-800">{banner.text}</div>
          </div>
        )}

        {audience === "shipper" && (
          <div className="card-base p-6" data-testid="dashboard-plan-card-payg">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gold mb-2">Pay as you go</div>
            <div className="font-display text-2xl font-bold text-navy">Load credits</div>
            <p className="text-sm text-slate-500 mt-1">Buy credits to unlock shipper tools without a monthly subscription.</p>
            <div className="grid sm:grid-cols-3 gap-3 mt-5">
              {Object.entries(packs).map(([key, p]) => (
                <button
                  key={key}
                  onClick={() => buyPack(key)}
                  disabled={busy === `pack-${key}`}
                  className="border border-slate-200 rounded-md p-4 text-left hover:border-gold hover:bg-amber-50/30 transition disabled:opacity-60"
                  data-testid={`dashboard-buy-${key}`}
                >
                  <span className="block font-display font-bold text-navy">{p.name}</span>
                  <span className="block text-xs text-slate-500 mt-1">${(p.price / p.credits).toFixed(2)} per load</span>
                  <span className="block font-display text-xl font-bold text-navy mt-3">{busy === `pack-${key}` ? "Opening..." : `$${p.price.toFixed(2)}`}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <div className="font-display text-xl font-bold text-navy mb-4">Plans for {audienceLabel}</div>
          <div className="grid md:grid-cols-3 gap-5">
            {audiencePlanKeys.map((key) => {
              const p = plans[key];
              if (!p) return null;
              return (
                <div key={key} className={`card-base p-7 relative ${p.popular ? "border-gold shadow-xl" : ""}`} data-testid={`dashboard-plan-card-${key}`}>
                  {p.popular && <div className="absolute -top-3 left-7 bg-gold text-navy text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded">Most popular</div>}
                  <div className="font-display text-2xl font-bold text-navy">{p.name}</div>
                  <div className="my-5 flex items-baseline gap-1">
                    <span className="font-display text-5xl font-bold tracking-tighter text-navy">${p.price}</span>
                    <span className="text-slate-500 text-sm">/mo</span>
                  </div>
                  <ul className="space-y-2.5 mb-7">
                    {p.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => subscribe(key)}
                    disabled={busy === `plan-${key}`}
                    className={`w-full ${p.popular ? "btn-primary" : "btn-secondary"} disabled:opacity-50`}
                    data-testid={`dashboard-subscribe-${key}`}
                  >
                    {busy === `plan-${key}` ? <><Loader2 size={14} className="animate-spin" /> Opening checkout</> : <>Get {p.name} <ArrowRight size={14} /></>}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  if (!stats) return <div className="text-slate-500">Loading…</div>;

  const isShipper = user.role === "shipper" || user.role === "ops_admin" || user.role === "super_admin";
  const isCarrier = user.role === "carrier_owner" || user.role === "dispatcher";
  const isDriver = user.role === "driver";

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-gold font-bold mb-2">{ROLE_LABEL[user.role]} Dashboard</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter text-navy">Hello, {user.name.split(" ")[0]}.</h1>
          <p className="text-slate-500 mt-1">Here's what's moving today.</p>
        </div>
        {isShipper && (
          <Link to="/app/loads/new" className="btn-primary" data-testid="dashboard-post-load-btn">
            <Truck size={16} /> Post a load
          </Link>
        )}
        {isCarrier && (
          <Link to="/app/board" className="btn-primary" data-testid="dashboard-browse-board-btn">
            Browse load board <ArrowRight size={16} />
          </Link>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {isShipper && <>
          <KPI label="Active Loads" value={stats.active_loads ?? 0} icon={Truck} accent />
          <KPI label="Pending" value={stats.pending_loads ?? 0} icon={Gavel} />
          <KPI label="Delivered" value={stats.delivered_loads ?? 0} icon={CheckCircle2} />
          <KPI label="Revenue" value={fmtMoney(stats.revenue ?? 0)} icon={DollarSign} />
        </>}
        {isCarrier && <>
          <KPI label="Assigned" value={stats.assigned_loads ?? 0} icon={Truck} accent />
          <KPI label="Open Bids" value={stats.open_bids ?? 0} icon={Gavel} />
          <KPI label="Delivered" value={stats.delivered_loads ?? 0} icon={CheckCircle2} />
          <KPI label="Revenue" value={fmtMoney(stats.revenue ?? 0)} icon={DollarSign} />
        </>}
        {isDriver && <>
          <KPI label="Assigned" value={stats.assigned_loads ?? 0} icon={Truck} accent />
          <KPI label="Delivered" value={stats.delivered_loads ?? 0} icon={CheckCircle2} />
          <KPI label="Unread" value={stats.unread_notifications ?? 0} icon={BellRing} />
        </>}
      </div>

      <div className="card-base">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-navy">Recent Loads</h3>
          <Link to="/app/loads" className="text-sm text-gold font-semibold hover:underline" data-testid="view-all-loads">View all →</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="table-base">
            <thead>
              <tr>
                <th>Load</th><th>Route</th><th>Equipment</th><th>Rate</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 && (
                <tr><td colSpan={5} className="text-center text-slate-400 py-10">No loads yet.</td></tr>
              )}
              {recent.map(l => (
                <tr key={l.load_id} className="cursor-pointer" data-testid={`recent-load-row-${l.load_id}`}>
                  <td><Link to={`/app/loads/${l.load_id}`} className="font-mono text-xs text-navy font-semibold">{l.load_id}</Link></td>
                  <td>{l.pickup_city}, {l.pickup_state} → {l.dropoff_city}, {l.dropoff_state}</td>
                  <td className="capitalize">{l.equipment_type}</td>
                  <td className="font-mono font-semibold">{fmtMoney(l.rate)}</td>
                  <td><span className={`badge ${l.status}`}>{STATUS_LABEL[l.status]}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
